import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, Heart, Leaf, MapPin, Mountain, Recycle, ShieldCheck, Sparkles } from "lucide-react";
import Reveal, { Magnetic, SectionHeading } from "@/components/Reveal";
import Particles, { GradientMesh } from "@/components/Particles";

const IMG_STORY = "https://images.unsplash.com/photo-1557296387-5358ad7997bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxza2luY2FyZSUyMG1vZGVsJTIwcm91dGluZSUyMHdvbWFuJTIwcG9ydHJhaXQlMjBmYWNlfGVufDB8fHx8MTc5MDc5MDgzNHww&ixlib=rb-4.1.0&q=85";
const IMG_BOTANICAL = "https://images.unsplash.com/photo-1615485500834-bc10199bc727?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHx0dXJtZXJpYyUyMGhlcmJhbCUyMGJvdGFuaWNhbCUyMGluZ3JlZGllbnQlMjBjb3NtZXRpY3N8ZW58MHx8fHwxNzkwNzkwODM0fDA&ixlib=rb-4.1.0&q=85";
const IMG_LAB = "https://images.unsplash.com/photo-1777450793530-99a0e7b7fc53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwc2VydW0lMjBkcm9wcGVyJTIwbGFiJTIwc2tpbmNhcmUlMjBwYWNrYWdpbmd8ZW58MHx8fHwxNzkwNzkwODI3fDA&ixlib=rb-4.1.0&q=85";

const SOURCING = [
  { place: "Pampore, Kashmir", item: "Crocus saffron, hand-picked at dawn" },
  { place: "Kannauj, UP", item: "Damask roses, copper-still distilled" },
  { place: "Mysore, Karnataka", item: "Sandalwood from regenerative estates" },
  { place: "Kerala Coast", item: "Cold-pressed coconut & bhringraj" },
  { place: "Satpura, MP", item: "Wild-crafted ashwagandha root" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative flex min-h-[80vh] items-center overflow-hidden pt-24">
        <GradientMesh />
        <Particles count={16} />
        <div className="relative mx-auto max-w-5xl px-4 py-20 text-center sm:px-6">
          <Reveal>
            <img src="/logo-full.jpg" alt="Mother Nature — Ayurvedic. Pure. Natural." className="mx-auto w-44 rounded-3xl sm:w-52" style={{ border: "1px solid var(--mn-glass-border)", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }} data-testid="about-brand-logo" />
          </Reveal>
          <Reveal>
            <p className="eyebrow mt-8">Our Story · Est. 2025 · Sihor, Gujarat</p>
          </Reveal>
          <h1 className="mt-6 font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <Reveal delay={0.15} y={60}><span className="gold-text italic">A beautician's curiosity.</span></Reveal>
            </span>
            <span className="block overflow-hidden pb-1">
              <Reveal delay={0.3} y={60}><span style={{ color: "var(--mn-mint)" }}>A hometown's herbs.</span></Reveal>
            </span>
          </h1>
          <Reveal delay={0.5}>
            <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed sm:text-base" style={{ color: "var(--mn-text2)" }}>
              Mother Nature began in 2025 in Sihor, a small town in Gujarat — not in a boardroom, but at the beauty table of Kiran Ben Vadher, a hometown beautician who spent years watching what herbs quietly did for her clients' skin. What started as curiosity became a craft. What became a craft became this house.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Story split */}
      <section className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal className="relative">
          <div className="overflow-hidden rounded-[2rem]" style={{ border: "1px solid var(--mn-glass-border)" }}>
            <img src={IMG_STORY} alt="The Mother Nature ritual" className="aspect-[4/5] w-full object-cover" loading="lazy" />
          </div>
          <div className="glass-card absolute -bottom-8 -right-2 w-56 overflow-hidden rounded-3xl p-2 sm:-right-8">
            <img src={IMG_BOTANICAL} alt="Golden botanicals" className="aspect-square w-full rounded-2xl object-cover" loading="lazy" />
          </div>
        </Reveal>
        <div className="pt-8 lg:pt-0">
          <SectionHeading eyebrow="Philosophy" title="Ancient wisdom. Modern science. Never one without the other." />
          <Reveal delay={0.15}>
            <p className="mt-6 text-sm leading-relaxed sm:text-base" style={{ color: "var(--mn-text2)" }}>
              Every formula still begins the way it did in Sihor — with a real person, a real skin concern, and a handful of herbs. Today each recipe is refined with science-backed actives and checked with clinical care, but the heart hasn't moved an inch: if it wouldn't be used on a neighbour's daughter, it doesn't ship.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Reveal delay={0.2} className="glass-card rounded-3xl p-6">
              <Leaf size={22} style={{ color: "var(--mn-gold)" }} />
              <p className="mt-3 font-serif text-lg">The Herbal Heart</p>
              <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "var(--mn-text2)" }}>Heritage botanicals, slow-brewed the way Kiran Ben learned in her Sihor practice — patiently, and by hand.</p>
            </Reveal>
            <Reveal delay={0.3} className="glass-card rounded-3xl p-6">
              <FlaskConical size={22} style={{ color: "var(--mn-mint)" }} />
              <p className="mt-3 font-serif text-lg">The Scientific Mind</p>
              <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "var(--mn-text2)" }}>Science-backed actives at studied doses — stabilized, buffered and dermatologist tested before they ever touch skin.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Founder spotlight */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: "var(--mn-bg2)" }} data-testid="founder-section">
        <Particles count={12} />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <SectionHeading eyebrow="The Hands Behind the Herbs" title="Meet the founder" align="center" />
          <Reveal delay={0.15} className="glass-card mx-auto mt-12 max-w-2xl rounded-[2.5rem] p-10 sm:p-14">
            <div className="mx-auto w-full max-w-md overflow-hidden rounded-[2rem] glow-gold" style={{ border: "1px solid var(--mn-glass-border)" }}>
              <img src="/founder.jpg" alt="Kiran Ben Vadher with her clients" className="aspect-[4/3] w-full object-cover" style={{ objectPosition: "center 25%" }} data-testid="founder-photo" />
            </div>
            <p className="mt-3 font-mono text-[0.58rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-text2)" }}>
              Kiran Ben (center) with her clients, post-ritual
            </p>
            <h3 className="mt-6 font-serif text-2xl sm:text-3xl" data-testid="founder-name">Kiran Ben Vadher</h3>
            <p className="mt-2 font-mono text-[0.62rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-mint)" }}>
              Founder · Beautician · Chief Formulator
            </p>
            <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed" style={{ color: "var(--mn-text2)" }}>
              Kiran Ben began as a small beautician in Sihor, Gujarat, where clients trusted her hands long before they trusted any label. Her hobby — grinding, infusing and blending local herbs — grew into formulas her clients refused to leave without. In 2025 she gave that hobby a name: Mother Nature.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <span className="flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ borderColor: "var(--mn-glass-border)", color: "var(--mn-text2)" }}>
                <MapPin size={12} style={{ color: "var(--mn-terra)" }} /> Sihor, Gujarat
              </span>
              <span className="flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ borderColor: "var(--mn-glass-border)", color: "var(--mn-text2)" }}>
                <Sparkles size={12} style={{ color: "var(--mn-gold)" }} /> Founded 2025
              </span>
              <span className="flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ borderColor: "var(--mn-glass-border)", color: "var(--mn-text2)" }}>
                <Heart size={12} style={{ color: "var(--mn-terra)" }} /> Woman-founded
              </span>
            </div>
            <p className="mt-8 font-serif text-lg italic" style={{ color: "var(--mn-gold)" }}>
              "Every jar still carries a little of that small Sihor room — where beauty was personal, and herbs were family."
            </p>
          </Reveal>
        </div>
      </section>

      {/* Sourcing */}
      <section id="sourcing" className="relative overflow-hidden py-20 lg:py-28">
        <Particles count={10} />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeading eyebrow="Ethical Sourcing" title="From Indian valleys to your vanity" sub="Every botanical is traced to a named farm, a named family, a named harvest." />
            <Reveal className="mt-8 overflow-hidden rounded-[2rem]" >
              <img src={IMG_LAB} alt="Lab precision meets botanical sourcing" className="aspect-[16/10] w-full object-cover" loading="lazy" style={{ border: "1px solid var(--mn-glass-border)", borderRadius: "2rem" }} />
            </Reveal>
          </div>
          <div className="flex flex-col justify-center">
            <ul className="space-y-4">
              {SOURCING.map((s, i) => (
                <Reveal key={s.place} delay={i * 0.08}>
                  <li className="glass-card flex items-start gap-4 rounded-2xl p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--mn-surface)" }}>
                      <Mountain size={17} style={{ color: "var(--mn-gold)" }} />
                    </span>
                    <div>
                      <p className="flex items-center gap-2 font-serif text-lg"><MapPin size={13} style={{ color: "var(--mn-terra)" }} /> {s.place}</p>
                      <p className="mt-0.5 text-xs" style={{ color: "var(--mn-text2)" }}>{s.item}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Visit the Studio */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8" data-testid="contact-section">
        <Reveal className="glass-card rounded-[2.5rem] p-8 sm:p-12">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="eyebrow">Visit · Call · WhatsApp</p>
              <h2 className="mt-3 font-serif text-2xl tracking-tight sm:text-3xl">
                Come say hello in <span className="gold-text italic">Sihor.</span>
              </h2>
              <p className="mt-4 flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: "var(--mn-text2)" }}>
                <MapPin size={16} className="mt-0.5 shrink-0" style={{ color: "var(--mn-terra)" }} />
                Mother Nature Studio, Bhavnagar–Rajkot Road, near PNB Bank, opposite Shivalik Prime, Sihor, Gujarat
              </p>
              <div className="mt-6 space-y-2.5">
                <a href="tel:+919662893341" data-testid="contact-phone-1" className="flex items-center gap-2.5 text-sm transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text)" }}>
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--mn-gold)" }}>Call</span> +91 96628 93341
                </a>
                <a href="tel:+917041442765" data-testid="contact-phone-2" className="flex items-center gap-2.5 text-sm transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text)" }}>
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--mn-gold)" }}>Call</span> +91 70414 42765
                </a>
                <a href="https://instagram.com/mothernature_official" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="flex items-center gap-2.5 text-sm transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text)" }}>
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--mn-gold)" }}>Insta</span> @mothernature_official
                </a>
              </div>
            </div>
            <div className="flex flex-col items-center gap-4">
              <Magnetic className="w-full max-w-xs">
                <a
                  href="https://wa.me/919662893341?text=Namaste!%20I%20have%20a%20question%20about%20Mother%20Nature%20products."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-7 py-4 text-sm"
                >
                  Chat on WhatsApp <ArrowRight size={15} />
                </a>
              </Magnetic>
              <p className="text-center font-mono text-[0.6rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-text2)" }}>
                Skin questions · orders · rituals — we reply personally
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Promise */}
      <section className="mx-auto max-w-7xl px-4 pb-6 pt-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-center gap-8">
          {[{ icon: ShieldCheck, label: "AYUSH & GMP Certified" }, { icon: Recycle, label: "Recyclable Glass Only" }, { icon: Leaf, label: "100% Vegan & Cruelty-Free" }].map((b) => (
            <span key={b.label} className="flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-text2)" }}>
              <b.icon size={15} style={{ color: "var(--mn-gold)" }} /> {b.label}
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24 pt-14 sm:px-6 lg:px-8">
        <Reveal className="glass-card relative overflow-hidden rounded-[2.5rem] px-8 py-16 text-center sm:px-14">
          <GradientMesh />
          <div className="relative">
            <h2 className="font-serif text-3xl tracking-tight sm:text-4xl">Begin your <span className="gold-text italic">ritual.</span></h2>
            <p className="mx-auto mt-3 max-w-md text-sm" style={{ color: "var(--mn-text2)" }}>Take the 60-second quiz and let Kiran Ben's herbs meet your skin.</p>
            <Magnetic className="mt-8">
              <Link to="/quiz" data-testid="about-quiz-cta" className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm">
                Take the Skin Quiz <ArrowRight size={15} />
              </Link>
            </Magnetic>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
